import { useCallback, useEffect, useMemo, useState } from "react";
import { FCX, getRem } from "@datassential/platform-ui-lib";
import { useTrendContext } from "@pages/Trend/TrendContext";
import GroupsColumnsLineChart from "@components/GroupsColumnsLineChart";
import ContentWidget from "@components/ContentWidget/ContentWidget";
import cn from "classnames";
import HistoricTrendChartInfo from "@pages/Trend/components/HistoricTrend/HtChartInfo";
import SearchFilters from "@pages/Trend/components/SearchFilters";
import HistoricTrendContext, { HistoricTrendContextType } from "@pages/Trend/components/HistoricTrend/HtContext";
import HistoricTrendTable from "@pages/Trend/components/HistoricTrend/HtTable";
import { HistoricTrendItem, HistoricTrendRequest, useHistoricTrend } from "@core/api/useHistoricTrend";
import Loader from "@components/Loader";
import { useCommonDataContext, useMetric } from "@core/CommonDataContext";
import { TREND_CHART_QUARTERS } from "@core/constants";
import { useSelectAmongContext } from "@vms/SelectAmong/SelectAmongContext";

const HistoricTrend: FCX<{
	isVisible?: boolean;
}> = ({
	isVisible,
}) => {
	const [activeSeriesId, setActiveSeriesId] = useState<HistoricTrendContextType['activeSeriesId']>(null);
	const [selectedItems, setSelectedItems] = useState<HistoricTrendContextType['selectedItems']>([]);
	const [data, setData] = useState<HistoricTrendContextType['data']>({});
	const [isLoading, setIsLoading] = useState<HistoricTrendContextType['isLoading']>(false);

	const { selectedMetric } = useTrendContext();

	const historicTrendContextValue = useMemo(() => ({
		activeSeriesId,
		setActiveSeriesId,
		selectedItems,
		setSelectedItems,
		data,
		setData,
		isLoading,
		setIsLoading,
	}), [activeSeriesId, setActiveSeriesId, selectedItems, setSelectedItems, data, setData, isLoading, setIsLoading]);

	const { amongPayload } = useSelectAmongContext();
	const payload = useMemo(
		(): HistoricTrendRequest => ({
			metric: selectedMetric as HistoricTrendRequest['metric'],
			...amongPayload,
		}),
		[selectedMetric, amongPayload]
	);

	const {
		data: rawData,
		isLoading: loadingRawData,
	} = useHistoricTrend(payload);

	useEffect(() => {
		setSelectedItems((prev) => {
			return prev.filter(i => rawData.hasOwnProperty(i.id));
		});
	}, [rawData]);

	useEffect(() => {
		setIsLoading(loadingRawData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loadingRawData]);

	useEffect(() => {
		setData(rawData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rawData]);

	const { surveys } = useCommonDataContext();
	const metric = useMetric(selectedMetric);

	const chartSurveys = useMemo(() => {
		const surveyIds = new Set<number>();

		selectedItems.forEach((item) => {
			const extendedItem = data[item.id]
			if (!extendedItem.surveys?.size) return

			extendedItem.surveys.forEach((surveyData) => {
				const surveyId = surveyData.surveyId;
				if (!surveyId) return;

				surveyIds.add(surveyId);
			});
		});

		return surveys?.filter((survey) => surveyIds.has(survey.id ?? 0));
	}, [data, selectedItems, surveys]);

	const {
		chartYears,
		chartMap
	} = useMemo(() => {
		const years = new Set<number>();
		const chartMap: Record<string, number> = {};

		chartSurveys?.forEach((survey) => {
			const name = survey.name;
			if (!name) return;

			// Names are like "Q1 2024"
			const year = name.split(' ')[1];
			if (!year) return;

			years.add(+year);
			chartMap[name] = survey.id ?? 0;
		});

		const chartYears = Array.from(years).sort((a, b) => a - b).map((year) => ({
			id: year.toString(),
			title: year.toString(),
		}));

		return {
			chartYears,
			chartMap,
		}
	}, [chartSurveys]);

	const generateItemValues = useCallback((extendedItem: HistoricTrendItem) => {
		return chartYears.flatMap((y) =>
			TREND_CHART_QUARTERS.map((q) => {
				const surveyName = `${q.title} ${y.title}`;
				const surveyId = chartMap[surveyName];

				return {
					groupId: y.id,
					columnId: q.id,
					value: extendedItem.surveys.get(surveyId)?.valueRaw ?? null,
				}
			})
		)
	}, [chartMap, chartYears])

	const chartData = useMemo(
		() => selectedItems.map((item) => {
			const extendedItem = data[item.id]
			return {
				id: item.id,
				color: item.color,
				title: extendedItem?.name?.toUpperCase() ?? "",
				values: generateItemValues(extendedItem)
			}
		}),
		[data, selectedItems, generateItemValues],
	);

	const chartItem = useMemo(() => {
		return chartData.length
			? <GroupsColumnsLineChart
				className="HistoricTrend__chart-container HistoricTrendChart"
				activeSeriesId={activeSeriesId}
				series={chartData}
				groups={chartYears}
				columns={TREND_CHART_QUARTERS}
				renderValue={(v) => typeof v === 'number' ? `${Math.round(v)}%` : '-'}
				renderTooltipTitle={(groupTitle, columnTitle) => <>
					{metric?.name?.toUpperCase()} <br/>
					<span style={{ fontSize: getRem(12) }}>
                        YE {columnTitle} {groupTitle}
                    </span>
				</>}
				testId="blockHistoricTrendChart"
			/>
			: <div
				className="HistoricTrend__chart-empty appears fast"
				data-testid="textHistoricTrendChartEmpty"
			>
				Please select items to see the chart!
			</div>;
	}, [activeSeriesId, chartData, chartYears, metric]);

	if (!isVisible) {
		return null;
	}

	if (isLoading) {
		return (
			<Loader/>
		);
	}

	return (
		<HistoricTrendContext.Provider value={historicTrendContextValue}>
			<div className="HistoricTrend">
				<div className="HistoricTrend__chart-view">
					<ContentWidget
						className={
							cn(
								"HistoricTrend__chart",
								"appears-right",
							)
						}
						modifiers={['pad-s']}
					>
						{chartItem}
					</ContentWidget>
					<ContentWidget
						className={
							cn(
								"HistoricTrend__chart-info",
								"appears-left",
							)
						}
						modifiers={['pad-none']}
					>
						<HistoricTrendChartInfo/>
					</ContentWidget>
				</div>
				<div className="HistoricTrend__table-view">
					<SearchFilters className="appears-top del-1"/>
					<HistoricTrendTable/>
				</div>
			</div>
		</HistoricTrendContext.Provider>
	);
};

export default HistoricTrend;